import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiAlertTriangle,
  FiBarChart2,
  FiCreditCard,
  FiDollarSign,
  FiPackage,
  FiShoppingBag,
  FiTrendingDown,
  FiTrendingUp,
  FiUsers,
  FiClock,
  FiCheckCircle,
  FiArrowRight,
  FiCalendar,
  FiActivity,
} from "react-icons/fi";

import StatCard from "../components/ui/StatCard";
import StatusBadge from "../components/ui/StatusBadge";
import { useGetDashboardQuery } from "../services/api";
import { money, shortDate } from "../utils/formatters";

const DashboardPage = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetDashboardQuery();
  const dashboard = data?.data;
  const [selectedPeriod, setSelectedPeriod] = useState('thisMonth');

  if (isLoading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner"></div>
        <p>Loading dashboard…</p>
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <div className="empty-state">
        <div className="empty-state__icon"><FiAlertTriangle /></div>
        <h3>Unable to load dashboard</h3>
        <p>Check that the API is running and the database is connected.</p>
      </div>
    );
  }

  const { stats, overdue_orders, low_stock_items, employee_productivity, top_customers, task_overview } = dashboard;

  // Quick Actions
  const quickActions = [
    { label: 'New Order', icon: <FiShoppingBag />, action: () => navigate('/orders/create'), color: '#e8702a' },
    { label: 'Add Customer', icon: <FiUsers />, action: () => navigate('/customers'), color: '#0f766e' },
    { label: 'Record Payment', icon: <FiCreditCard />, action: () => navigate('/payments'), color: '#188b5a' },
    { label: 'View Reports', icon: <FiBarChart2 />, action: () => navigate('/reports'), color: '#7c3aed' },
  ];

  return (
    <div className="modern-dashboard">
      {/* Welcome Section */}
      <div className="dashboard-welcome">
        <div>
          <h1 className="dashboard-welcome__title">Welcome back! 👋</h1>
          <p className="dashboard-welcome__subtitle">
            Here's what's happening with your business today
          </p>
        </div>
        <div className="dashboard-quick-actions">
          {quickActions.map((action, idx) => (
            <button
              key={idx}
              className="quick-action-btn"
              onClick={action.action}
              style={{ '--action-color': action.color }}
            >
              {action.icon}
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Stats Grid - Enhanced */}
      <div className="stats-grid-modern">
        <div className="stat-card-modern stat-card-modern--primary">
          <div className="stat-card-modern__header">
            <span className="stat-card-modern__label">Total Revenue</span>
            <div className="stat-card-modern__icon" style={{ background: 'rgba(24, 139, 90, 0.1)' }}>
              <FiDollarSign style={{ color: '#188b5a' }} />
            </div>
          </div>
          <div className="stat-card-modern__value">{money(stats.revenue)}</div>
          <div className="stat-card-modern__footer">
            <span className="stat-card-modern__trend stat-card-modern__trend--up">
              <FiTrendingUp /> +12% from last month
            </span>
          </div>
        </div>

        <div className="stat-card-modern">
          <div className="stat-card-modern__header">
            <span className="stat-card-modern__label">Unpaid Balances</span>
            <div className="stat-card-modern__icon" style={{ background: 'rgba(194, 133, 16, 0.1)' }}>
              <FiCreditCard style={{ color: '#c28510' }} />
            </div>
          </div>
          <div className="stat-card-modern__value">{money(stats.unpaid_balances)}</div>
          <div className="stat-card-modern__footer">
            <span className="stat-card-modern__meta">
              Requires follow-up
            </span>
          </div>
        </div>

        <div className="stat-card-modern">
          <div className="stat-card-modern__header">
            <span className="stat-card-modern__label">Total Orders</span>
            <div className="stat-card-modern__icon" style={{ background: 'rgba(232, 112, 42, 0.1)' }}>
              <FiShoppingBag style={{ color: '#e8702a' }} />
            </div>
          </div>
          <div className="stat-card-modern__value">{stats.total_orders}</div>
          <div className="stat-card-modern__footer">
            <span className="stat-card-modern__meta">
              {stats.overdue_orders} overdue
            </span>
          </div>
        </div>

        <div className="stat-card-modern">
          <div className="stat-card-modern__header">
            <span className="stat-card-modern__label">Customers</span>
            <div className="stat-card-modern__icon" style={{ background: 'rgba(15, 118, 110, 0.1)' }}>
              <FiUsers style={{ color: '#0f766e' }} />
            </div>
          </div>
          <div className="stat-card-modern__value">{stats.total_customers}</div>
          <div className="stat-card-modern__footer">
            <span className="stat-card-modern__trend stat-card-modern__trend--up">
              <FiTrendingUp /> +5 this week
            </span>
          </div>
        </div>
      </div>

      {/* Task Overview - Enhanced */}
      <div className="task-overview-modern">
        <div className="task-overview-modern__header">
          <div>
            <h3 className="task-overview-modern__title">Task Status</h3>
            <p className="task-overview-modern__subtitle">Team productivity overview</p>
          </div>
          <button className="task-overview-modern__link" onClick={() => navigate('/tasks')}>
            View All <FiArrowRight />
          </button>
        </div>
        <div className="task-overview-modern__grid">
          <div className="task-status-card task-status-card--todo">
            <div className="task-status-card__icon">
              <FiClock />
            </div>
            <div className="task-status-card__content">
              <span className="task-status-card__label">To Do</span>
              <span className="task-status-card__value">{task_overview.todo}</span>
            </div>
          </div>
          <div className="task-status-card task-status-card--progress">
            <div className="task-status-card__icon">
              <FiActivity />
            </div>
            <div className="task-status-card__content">
              <span className="task-status-card__label">In Progress</span>
              <span className="task-status-card__value">{task_overview.in_progress}</span>
            </div>
          </div>
          <div className="task-status-card task-status-card--done">
            <div className="task-status-card__icon">
              <FiCheckCircle />
            </div>
            <div className="task-status-card__content">
              <span className="task-status-card__label">Completed</span>
              <span className="task-status-card__value">{task_overview.done}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-content-grid">
        {/* Overdue Orders */}
        <section className="panel-modern">
          <div className="panel-modern__header">
            <div>
              <h3 className="panel-modern__title">Overdue Orders</h3>
              <p className="panel-modern__subtitle">{overdue_orders.length} require attention</p>
            </div>
            {overdue_orders.length > 0 && (
              <span className="badge-modern badge-modern--danger">Urgent</span>
            )}
          </div>
          <div className="panel-modern__content">
            {overdue_orders.length === 0 ? (
              <div className="empty-state-modern">
                <FiCheckCircle />
                <p>All orders are on track!</p>
              </div>
            ) : (
              overdue_orders.map((order) => (
                <div
                  key={order.id}
                  className="list-item-modern"
                  onClick={() => navigate(`/orders/${order.id}`)}
                >
                  <div className="list-item-modern__content">
                    <div className="list-item-modern__header">
                      <strong>{order.title}</strong>
                      <span className="badge-modern badge-modern--sm badge-modern--danger">
                        Overdue
                      </span>
                    </div>
                    <div className="list-item-modern__meta">
                      <span><FiUsers /> {order.customer_name}</span>
                      <span><FiCalendar /> Due {shortDate(order.deadline)}</span>
                    </div>
                  </div>
                  <div className="list-item-modern__value">
                    {money(order.remaining_balance)}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Top Customers */}
        <section className="panel-modern">
          <div className="panel-modern__header">
            <div>
              <h3 className="panel-modern__title">Top Customers</h3>
              <p className="panel-modern__subtitle">Your best clients</p>
            </div>
            <button className="panel-modern__link" onClick={() => navigate('/customers')}>
              View All <FiArrowRight />
            </button>
          </div>
          <div className="panel-modern__content">
            {top_customers.length === 0 ? (
              <div className="empty-state-modern">
                <FiUsers />
                <p>No customers yet</p>
              </div>
            ) : (
              top_customers.map((customer, i) => (
                <div key={customer.id} className="list-item-modern">
                  <div className="list-item-modern__content">
                    <div className="customer-rank">{i + 1}</div>
                    <div>
                      <strong>{customer.name}</strong>
                      <span className="list-item-modern__meta">
                        {customer.total_orders} order{customer.total_orders !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                  <div className="list-item-modern__value list-item-modern__value--success">
                    {money(customer.total_spend)}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Team Productivity */}
        <section className="panel-modern">
          <div className="panel-modern__header">
            <div>
              <h3 className="panel-modern__title">Team Productivity</h3>
              <p className="panel-modern__subtitle">Performance metrics</p>
            </div>
            <button className="panel-modern__link" onClick={() => navigate('/employees')}>
              View All <FiArrowRight />
            </button>
          </div>
          <div className="panel-modern__content">
            {employee_productivity.length === 0 ? (
              <div className="empty-state-modern">
                <FiUsers />
                <p>No employees yet</p>
              </div>
            ) : (
              employee_productivity.map((emp) => (
                <div key={emp.id} className="list-item-modern">
                  <div className="list-item-modern__content">
                    <div className="employee-avatar">
                      {emp.first_name?.[0]}{emp.last_name?.[0]}
                    </div>
                    <div>
                      <strong>{emp.first_name} {emp.last_name}</strong>
                      <span className="list-item-modern__meta">
                        {emp.role} • {emp.active_orders} active orders
                      </span>
                    </div>
                  </div>
                  <div className="productivity-score">
                    <div className="productivity-score__value">{emp.productivity_score}%</div>
                    <div className="productivity-score__bar">
                      <div 
                        className="productivity-score__fill"
                        style={{ width: `${emp.productivity_score}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Low Stock */}
        <section className="panel-modern">
          <div className="panel-modern__header">
            <div>
              <h3 className="panel-modern__title">Stock Alerts</h3>
              <p className="panel-modern__subtitle">Low inventory items</p>
            </div>
            {low_stock_items.length > 0 && (
              <span className="badge-modern badge-modern--warning">{low_stock_items.length}</span>
            )}
          </div>
          <div className="panel-modern__content">
            {low_stock_items.length === 0 ? (
              <div className="empty-state-modern">
                <FiPackage />
                <p>All stock levels healthy</p>
              </div>
            ) : (
              low_stock_items.map((item) => (
                <div key={item.id} className="list-item-modern">
                  <div className="list-item-modern__content">
                    <div className="stock-icon">
                      <FiPackage />
                    </div>
                    <div>
                      <strong>{item.name}</strong>
                      <span className="list-item-modern__meta">
                        {item.category} • Min: {item.min_level} {item.unit}
                      </span>
                    </div>
                  </div>
                  <div className="list-item-modern__value list-item-modern__value--danger">
                    {item.quantity} left
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default DashboardPage;
